import { useState, useCallback, useEffect } from 'react'
import type { PromoCode, PromoRedemption } from '../types'

const PROMO_STORAGE_KEY = 'aerozeep_promo_codes'
const REDEMPTION_STORAGE_KEY = 'aerozeep_promo_redemptions'

// Mock promo codes
const INITIAL_PROMO_CODES: PromoCode[] = [
  {
    id: 'promo-001',
    code: 'WELCOME10',
    type: 'percentage',
    value: 10,
    minAmount: 10000,
    maxDiscount: 5000,
    usageLimit: 100,
    usageCount: 23,
    status: 'active',
    createdAt: '2024-01-01T00:00:00',
  },
  {
    id: 'promo-002',
    code: 'BONUS5000',
    type: 'fixed_amount',
    value: 5000,
    minAmount: 20000,
    usageLimit: 50,
    usageCount: 12,
    status: 'active',
    createdAt: '2024-01-15T00:00:00',
  },
  {
    id: 'promo-003',
    code: 'NEWYEAR2024',
    type: 'percentage',
    value: 15,
    minAmount: 15000,
    maxDiscount: 10000,
    expiresAt: '2024-12-31T23:59:59',
    usageLimit: 200,
    usageCount: 87,
    status: 'active',
    createdAt: '2024-01-01T00:00:00',
  },
]

export function usePromoCodes() {
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>(INITIAL_PROMO_CODES)
  const [redemptions, setRedemptions] = useState<PromoRedemption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load data from storage
  const loadData = useCallback(() => {
    try {
      const storedCodes = localStorage.getItem(PROMO_STORAGE_KEY)
      const storedRedemptions = localStorage.getItem(REDEMPTION_STORAGE_KEY)

      if (storedCodes) {
        setPromoCodes(JSON.parse(storedCodes))
      } else {
        localStorage.setItem(
          PROMO_STORAGE_KEY,
          JSON.stringify(INITIAL_PROMO_CODES),
        )
      }

      if (storedRedemptions) {
        setRedemptions(JSON.parse(storedRedemptions))
      }
    } catch (err) {
      console.error('Failed to load promo codes', err)
    }
  }, [])

  // Save promo codes to storage
  const savePromoCodes = useCallback((newCodes: PromoCode[]) => {
    try {
      localStorage.setItem(PROMO_STORAGE_KEY, JSON.stringify(newCodes))
      setPromoCodes(newCodes)
    } catch (err) {
      console.error('Failed to save promo codes', err)
    }
  }, [])

  // Save redemptions to storage
  const saveRedemptions = useCallback((newRedemptions: PromoRedemption[]) => {
    try {
      localStorage.setItem(
        REDEMPTION_STORAGE_KEY,
        JSON.stringify(newRedemptions),
      )
      setRedemptions(newRedemptions)
    } catch (err) {
      console.error('Failed to save redemptions', err)
    }
  }, [])

  // Validate promo code
  const validatePromoCode = useCallback(
    async (
      code: string,
      amount: number,
    ): Promise<{ valid: boolean; discount: number; error?: string }> => {
      setIsLoading(true)
      setError(null)

      try {
        await new Promise((resolve) => setTimeout(resolve, 800))

        const promo = promoCodes.find(
          (p) => p.code.toUpperCase() === code.toUpperCase(),
        )

        if (!promo) {
          return { valid: false, discount: 0, error: 'Invalid promo code' }
        }

        if (promo.status !== 'active') {
          return {
            valid: false,
            discount: 0,
            error: 'This promo code is no longer active',
          }
        }

        if (promo.expiresAt && new Date(promo.expiresAt) < new Date()) {
          return {
            valid: false,
            discount: 0,
            error: 'This promo code has expired',
          }
        }

        if (promo.usageLimit && promo.usageCount >= promo.usageLimit) {
          return {
            valid: false,
            discount: 0,
            error: 'This promo code has reached its usage limit',
          }
        }

        if (promo.minAmount && amount < promo.minAmount) {
          return {
            valid: false,
            discount: 0,
            error: `Minimum amount of ₦${promo.minAmount.toLocaleString()} required`,
          }
        }

        let discount = 0
        if (promo.type === 'percentage') {
          discount = Math.round((amount * promo.value) / 100)
          if (promo.maxDiscount) {
            discount = Math.min(discount, promo.maxDiscount)
          }
        } else {
          discount = promo.value
        }

        return { valid: true, discount }
      } catch (err) {
        setError('Failed to validate promo code')
        return { valid: false, discount: 0, error: 'Validation failed' }
      } finally {
        setIsLoading(false)
      }
    },
    [promoCodes],
  )

  // Redeem promo code
  const redeemPromoCode = useCallback(
    async (code: string, amount: number): Promise<PromoRedemption> => {
      setIsLoading(true)
      setError(null)

      try {
        const validation = await validatePromoCode(code, amount)

        if (!validation.valid) {
          throw new Error(validation.error || 'Invalid promo code')
        }

        const promo = promoCodes.find(
          (p) => p.code.toUpperCase() === code.toUpperCase(),
        )!

        // Update usage count
        const updatedCodes = promoCodes.map((p) =>
          p.id === promo.id ? { ...p, usageCount: p.usageCount + 1 } : p,
        )
        savePromoCodes(updatedCodes)

        // Create redemption record
        const redemption: PromoRedemption = {
          id: `redemption-${Date.now()}`,
          userId: 'user-001',
          promoCode: promo,
          amount: validation.discount,
          appliedAt: new Date().toISOString(),
        }

        const updatedRedemptions = [redemption, ...redemptions]
        saveRedemptions(updatedRedemptions)

        return redemption
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to redeem promo code',
        )
        throw err
      } finally {
        setIsLoading(false)
      }
    },
    [
      promoCodes,
      redemptions,
      validatePromoCode,
      savePromoCodes,
      saveRedemptions,
    ],
  )

  // Get user redemptions
  const getUserRedemptions = useCallback(
    (userId: string) => {
      return redemptions.filter((r) => r.userId === userId)
    },
    [redemptions],
  )

  // Initialize on mount
  useEffect(() => {
    loadData()
  }, [loadData])

  return {
    promoCodes,
    redemptions,
    isLoading,
    error,
    validatePromoCode,
    redeemPromoCode,
    getUserRedemptions,
  }
}
